import React, { useState, useEffect } from "react";
import { administradoresService } from "../services/api";
import { useNavigate } from "react-router-dom";
import {
  Card,
  Table,
  Form,
  Button,
  Pagination,
  InputGroup,
  Modal,
} from "react-bootstrap";
import {
  FaSearch,
  FaEdit,
  FaSave,
  FaTimes,
  FaPlus,
  FaTrash,
} from "react-icons/fa";
import { toast } from "react-toastify";

const PAGE_SIZE = 8;

const AdministradoresList = () => {
  const [administradores, setAdministradores] = useState([]);
  const [search, setSearch] = useState("");
  const [activo, setActivo] = useState("true");
  const [page, setPage] = useState(1);
  const [orderBy, setOrderBy] = useState(0);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: PAGE_SIZE,
    total: 0,
    pages: 1,
  });
  const [editingAdmin, setEditingAdmin] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteAdmin, setDeleteAdmin] = useState(null);
  const [editForm, setEditForm] = useState({
    name: "",
    phone: "",
  });
  const [addForm, setAddForm] = useState({
    name: "",
    phone: "",
  });
  const navigate = useNavigate();

  useEffect(() => {
    fetchAdministradores();
    // eslint-disable-next-line
  }, [page, orderBy, activo]);

  const fetchAdministradores = async () => {
    setLoading(true);
    try {
      const res = await administradoresService.buscar({
        search,
        activo,
        page,
        limit: PAGE_SIZE,
        orderBy,
      });
      setAdministradores(res.data.administradores);
      if (res.data.pagination) {
        setPagination(res.data.pagination);
      } else {
        setPagination((prev) => ({
          ...prev,
          total: res.data.administradores.length,
          pages: 1,
        }));
      }
    } catch (err) {
      setAdministradores([]);
      setPagination({ page: 1, limit: PAGE_SIZE, total: 0, pages: 1 });
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    fetchAdministradores();
  };

  const handleActivoChange = (e) => {
    setActivo(e.target.value);
    setPage(1);
  };

  const handleOrder = (colIdx) => {
    setOrderBy(colIdx);
    setPage(1);
  };

  const handleEdit = (admin) => {
    setEditingAdmin(admin);
    setEditForm({
      name: admin.NAME || "",
      phone: admin.PHONE || "",
    });
    setShowEditModal(true);
  };

  const handleSaveEdit = async () => {
    try {
      await administradoresService.update(editingAdmin.ID, editForm);
      toast.success("Administrador actualizado correctamente");
      setShowEditModal(false);
      setEditingAdmin(null);
      fetchAdministradores();
    } catch (err) {
      toast.error(
        err.response?.data?.message || "Error al actualizar administrador"
      );
    }
  };

  const handleCancelEdit = () => {
    setShowEditModal(false);
    setEditingAdmin(null);
    setEditForm({ name: "", phone: "" });
  };

  const handleAdd = () => {
    setAddForm({ name: "", phone: "" });
    setShowAddModal(true);
  };

  const handleSaveAdd = async () => {
    try {
      await administradoresService.create(addForm);
      toast.success("Administrador creado correctamente");
      setShowAddModal(false);
      setAddForm({ name: "", phone: "" });
      fetchAdministradores();
    } catch (err) {
      toast.error(
        err.response?.data?.message || "Error al crear administrador"
      );
    }
  };

  const handleCancelAdd = () => {
    setShowAddModal(false);
    setAddForm({ name: "", phone: "" });
  };

  const handleDelete = (admin) => {
    setDeleteAdmin(admin);
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = async () => {
    try {
      await administradoresService.delete(deleteAdmin.ID);
      toast.success("Administrador eliminado correctamente");
      setShowDeleteModal(false);
      setDeleteAdmin(null);
      fetchAdministradores();
    } catch (err) {
      toast.error(
        err.response?.data?.message || "Error al eliminar administrador"
      );
    }
  };

  const handleCancelDelete = () => {
    setShowDeleteModal(false);
    setDeleteAdmin(null);
  };

  return (
    <>
      <Card className="mt-4">
        <Card.Header>
          <div className="d-flex justify-content-between align-items-center">
            <h4>Administradores</h4>
            <Button variant="primary" onClick={handleAdd}>
              <FaPlus /> Añadir Administrador
            </Button>
          </div>
        </Card.Header>
        <Card.Body>
          <Form onSubmit={handleSearch} className="mb-3">
            <div className="row g-3">
              <div className="col-md-9">
                <InputGroup>
                  <Form.Control
                    type="text"
                    placeholder="Buscar por nombre o teléfono..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                  />
                  <Button type="submit" variant="primary">
                    <FaSearch />
                  </Button>
                </InputGroup>
              </div>
              <div className="col-md-3">
                <Form.Select value={activo} onChange={handleActivoChange}>
                  <option value="">Todos los estados</option>
                  <option value="true">Activo</option>
                  <option value="false">Inactivo</option>
                </Form.Select>
              </div>
            </div>
          </Form>
          {/* Vista de tabla para desktop */}
          <div className="d-none d-md-block">
            <Table striped bordered hover responsive>
              <thead>
                <tr>
                  <th
                    onClick={() => handleOrder(1)}
                    style={{ cursor: "pointer" }}
                  >
                    NOMBRE
                  </th>
                  <th
                    onClick={() => handleOrder(3)}
                    style={{ cursor: "pointer" }}
                  >
                    TELÉFONO
                  </th>
                  <th>ESTADO</th>
                  <th>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {administradores.length === 0 && (
                  <tr>
                    <td colSpan={4} className="text-center">
                      No hay administradores
                    </td>
                  </tr>
                )}
                {administradores.map((admin) => (
                  <tr key={admin.ID}>
                    <td>{admin.NAME}</td>
                    <td>{admin.PHONE}</td>
                    <td>
                      <span
                        className={`badge ${
                          admin.ACTIVO ? "bg-success" : "bg-danger"
                        }`}
                      >
                        {admin.ACTIVO ? "Activo" : "Inactivo"}
                      </span>
                    </td>
                    <td>
                      <Button
                        variant="outline-primary"
                        size="sm"
                        onClick={() => handleEdit(admin)}
                        title="Editar"
                        className="me-2"
                      >
                        <FaEdit />
                      </Button>
                      {admin.ACTIVO && (
                        <Button
                          variant="outline-danger"
                          size="sm"
                          onClick={() => handleDelete(admin)}
                          title="Eliminar"
                        >
                          <FaTrash />
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
          {/* Vista de cards para móvil */}
          <div className="d-md-none">
            {administradores.length === 0 ? (
              <div className="text-center py-4">No hay administradores</div>
            ) : (
              <div className="gastos-cards">
                {administradores.map((admin) => (
                  <Card key={admin.ID} className="mb-3 gasto-card">
                    <Card.Body>
                      <div className="d-flex justify-content-between align-items-start mb-2">
                        <div className="flex-grow-1">
                          <h6 className="mb-1">{admin.NAME}</h6>
                        </div>
                        <div className="d-flex align-items-center">
                          <span
                            className={`badge ${
                              admin.ACTIVO ? "bg-success" : "bg-danger"
                            } me-2`}
                          >
                            {admin.ACTIVO ? "Activo" : "Inactivo"}
                          </span>
                          <Button
                            variant="outline-primary"
                            size="sm"
                            onClick={() => handleEdit(admin)}
                            title="Editar"
                            className="me-2"
                          >
                            <FaEdit />
                          </Button>
                          {admin.ACTIVO && (
                            <Button
                              variant="outline-danger"
                              size="sm"
                              onClick={() => handleDelete(admin)}
                              title="Eliminar"
                            >
                              <FaTrash />
                            </Button>
                          )}
                        </div>
                      </div>
                      <div className="row g-2 mb-2">
                        <div className="col-12">
                          <small className="text-muted d-block">
                            Teléfono:
                          </small>
                          <span>{admin.PHONE}</span>
                        </div>
                      </div>
                    </Card.Body>
                  </Card>
                ))}
              </div>
            )}
          </div>
          {/* Paginación mejorada */}
          {pagination.pages > 1 && (
            <div className="d-flex justify-content-center mt-3">
              <Pagination>
                <Pagination.First
                  onClick={() => setPage(1)}
                  disabled={pagination.page === 1}
                />
                <Pagination.Prev
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={pagination.page === 1}
                />
                {/* Páginas dinámicas centradas en la actual */}
                {Array.from({ length: pagination.pages }, (_, i) => i + 1)
                  .filter(
                    (pageNum) =>
                      pageNum === 1 ||
                      pageNum === pagination.pages ||
                      (pageNum >= pagination.page - 2 &&
                        pageNum <= pagination.page + 2)
                  )
                  .map((pageNum, idx, arr) => {
                    // Añadir puntos suspensivos si hay salto
                    if (idx > 0 && pageNum - arr[idx - 1] > 1) {
                      return [
                        <Pagination.Ellipsis
                          key={`ellipsis-${pageNum}`}
                          disabled
                        />,
                        <Pagination.Item
                          key={pageNum}
                          active={pageNum === pagination.page}
                          onClick={() => setPage(pageNum)}
                        >
                          {pageNum}
                        </Pagination.Item>,
                      ];
                    }
                    return (
                      <Pagination.Item
                        key={pageNum}
                        active={pageNum === pagination.page}
                        onClick={() => setPage(pageNum)}
                      >
                        {pageNum}
                      </Pagination.Item>
                    );
                  })}
                <Pagination.Next
                  onClick={() =>
                    setPage((p) => Math.min(pagination.pages, p + 1))
                  }
                  disabled={pagination.page === pagination.pages}
                />
                <Pagination.Last
                  onClick={() => setPage(pagination.pages)}
                  disabled={pagination.page === pagination.pages}
                />
              </Pagination>
            </div>
          )}
          {/* Leyenda informativa */}
          <div className="mt-3">
            <Card className="mb-0">
              <Card.Body>
                <h6 className="mb-2">Información de administradores bot</h6>
                <p className="mb-2">
                  Los administradores del bot pueden gestionar eventos de trabajo y asistencia desde el bot. El teléfono debe incluir el <strong>prefijo internacional</strong> (por ejemplo, <strong>34</strong> para España). Si el teléfono indicado coincide con el de un empleado, <strong>ese usuario actuará como administrador</strong>.
                </p>
                <p className="mb-1"><strong>Funciones disponibles del menú</strong>:</p>
                <ol className="mb-3">
                  <li>Listar los próximos eventos (hoy y mañana).</li>
                  <li>Buscar eventos por fechas, número, nombre del evento, nombre o ID del empleado.</li>
                  <li>Cancelar un evento.</li>
                  <li>Cambiar el día de un evento.</li>
                </ol>
                <p className="mb-1"><strong>Leyenda de acciones</strong>:</p>
                <div className="d-flex flex-column gap-1">
                  <div>
                    <span className="badge bg-primary me-2"><FaPlus /> Añadir</span>
                    Crea un nuevo administrador (indique nombre y teléfono con prefijo).
                  </div>
                  <div>
                    <span className="badge bg-warning text-dark me-2"><FaEdit /> Editar</span>
                    Modifica nombre o teléfono. Si el usuario está inactivo, permite reactivarlo.
                  </div>
                  <div>
                    <span className="badge bg-danger me-2"><FaTrash /> Eliminar</span>
                    Desactiva al administrador seleccionado.
                  </div>
                </div>
              </Card.Body>
            </Card>
          </div>
        </Card.Body>
      </Card>

      {/* Modal de edición */}
      <Modal show={showEditModal} onHide={handleCancelEdit}>
        <Modal.Header closeButton>
          <Modal.Title>Editar Administrador</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>ID</Form.Label>
              <Form.Control
                type="text"
                value={editingAdmin?.ID || ""}
                disabled
                readOnly
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Nombre</Form.Label>
              <Form.Control
                type="text"
                value={editForm.name}
                onChange={(e) =>
                  setEditForm({ ...editForm, name: e.target.value })
                }
                placeholder="Ingrese el nombre"
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Teléfono</Form.Label>
              <Form.Control
                type="text"
                value={editForm.phone}
                onChange={(e) =>
                  setEditForm({ ...editForm, phone: e.target.value })
                }
                placeholder="Ingrese el teléfono"
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCancelEdit}>
            <FaTimes /> Cancelar
          </Button>
          {!editingAdmin?.ACTIVO && (
            <Button
              variant="success"
              onClick={async () => {
                try {
                  await administradoresService.update(editingAdmin.ID, {
                    ...editForm,
                    activo: 1,
                  });
                  toast.success("Administrador reactivado correctamente");
                  setShowEditModal(false);
                  setEditingAdmin(null);
                  fetchAdministradores();
                } catch (err) {
                  toast.error(
                    err.response?.data?.message ||
                      "Error al reactivar administrador"
                  );
                }
              }}
            >
              Reactivar
            </Button>
          )}
          <Button variant="primary" onClick={handleSaveEdit}>
            <FaSave /> Guardar
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Modal de añadir */}
      <Modal show={showAddModal} onHide={handleCancelAdd}>
        <Modal.Header closeButton>
          <Modal.Title>Añadir Administrador</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Nombre</Form.Label>
              <Form.Control
                type="text"
                value={addForm.name}
                onChange={(e) =>
                  setAddForm({ ...addForm, name: e.target.value })
                }
                placeholder="Ingrese el nombre"
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Teléfono (Añadir prefijo ej 34)</Form.Label>
              <Form.Control
                type="text"
                value={addForm.phone}
                onChange={(e) =>
                  setAddForm({ ...addForm, phone: e.target.value })
                }
                placeholder="Ingrese el teléfono"
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCancelAdd}>
            <FaTimes /> Cancelar
          </Button>
          <Button variant="primary" onClick={handleSaveAdd}>
            <FaSave /> Guardar
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Modal de confirmación de eliminación */}
      <Modal show={showDeleteModal} onHide={handleCancelDelete}>
        <Modal.Header closeButton>
          <Modal.Title>Confirmar Eliminación</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>
            ¿Está seguro que desea eliminar al administrador{" "}
            <strong>{deleteAdmin?.NAME}</strong>?
          </p>
          <p className="text-danger">
            <small>Esta acción no se puede deshacer.</small>
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCancelDelete}>
            <FaTimes /> Cancelar
          </Button>
          <Button variant="danger" onClick={handleConfirmDelete}>
            <FaTrash /> Eliminar
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default AdministradoresList;
